import { useState } from "react";
import { Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { http, formatApiError } from "@/lib/api";

const CARRIERS = ["USPS", "UPS", "FedEx", "DHL"];

export function ShipDialog({ order, headers, onShipped }) {
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState("USPS");
  const [tracking, setTracking] = useState("");
  const [loading, setLoading] = useState(false);
  const sid = order.session_id;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post(`/admin/orders/${sid}/ship`, { carrier, tracking_number: tracking.replace(/\s/g, "") }, { headers });
      onShipped(data);
      setOpen(false);
      toast.success(data?.shipping_email?.startsWith("sent") ? "Marked as shipped — customer emailed" : "Marked as shipped (email not sent)");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`ship-btn-${sid}`} className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#281715] text-[#FAF7F2] px-3 h-8 text-xs hover:bg-[#4A2E2B] transition-colors duration-200">
          <Truck className="w-3.5 h-3.5" /> Mark as shipped
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#FAF7F2] rounded-[28px] border-[#E6D7CC] max-w-md" data-testid="ship-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl font-normal">Mark as shipped</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6E504A]">{order.customer_email ? `We'll email ${order.customer_email} their tracking number.` : "No customer email on this order — it will be marked shipped without an email."}</p>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="flex flex-wrap gap-2">
            {CARRIERS.map((c) => (
              <button type="button" key={c} onClick={() => setCarrier(c)} data-testid={`carrier-${c.toLowerCase()}`}
                className={`rounded-full px-4 h-10 text-sm border transition-colors duration-200 ${carrier === c ? "bg-[#281715] text-[#FAF7F2] border-[#281715]" : "border-[#E6D7CC] hover:border-[#4A2E2B]"}`}>{c}</button>
            ))}
          </div>
          <input required value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number" pattern="[A-Za-z0-9 ]{6,40}"
            data-testid="tracking-input" className="w-full h-12 rounded-full border border-[#E6D7CC] bg-white px-5 font-mono-lumi text-sm outline-none focus:border-[#4A2E2B]" />
          <button disabled={loading} data-testid="ship-submit-btn" className="w-full h-12 rounded-full bg-[#281715] text-[#FAF7F2] flex items-center justify-center hover:bg-[#4A2E2B] transition-colors duration-200">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & notify customer"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
